import { useCallback, useEffect, useRef } from 'react';
import { MultiPolygon, Polygon } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { Feature } from 'ol';

import { axios } from 'configurations/axios';
import { useSessionStore } from 'session/useSessionStore';
import { useSnackbarStore } from 'components/geopostSnackbar/store/useSnackbarStore';
import { useLine3DNavigationStore} from '../stores/useLine3DNavigationStore';
import { useMapStore} from '../stores/useMapStore';
import {IAbsoluteGain, IRangeTrace, IScale, useSeismicStore} from '../stores/useSeismicStore';
import {LineType} from '../models/enums/LineType';
import {IResponseAPI} from 'models/interfaces/IResponseAPI';
import { SeismicCalculatorFactory } from '../models/classes/SeismicCalculatorFactory';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { useTileSize } from './useTileSize';
import { usePrepareRender } from './usePrepareRender';
import { SurveyType } from '../models/enums/SurveyType';
import { IGeopostSeismicLayerParams } from '../models/interfaces/IGeopostSeismicLayerParams';
import { ZSliceTileSourceStrategy } from '../models/classes/TileSourceStrategies/ZSliceTileSourceStrategy';
import { LineTileSourceStrategy } from '../models/classes/TileSourceStrategies/LineTileSourceStrategy';
import { useGetSliceRotation } from './useGetSliceRotation';
import { ISurveyMetadata } from '../models/interfaces/ISurveyMetadata';
import { useZSliceStore } from '../stores/useZSliceStore';
import { calculateXPosition, CalculateXPositionWithIncrement, getSurveyExtentDistance, getTrace, getXByTrace } from '../utils/calculateScreenValuesUtils';
import { IResolutionsResponse } from '../models/interfaces/IResolutionsResponse';
import { UseAttributesStore } from '../stores/useAttributesStore';
import { useFeatureFlags } from './useFeatureFlags';
import { useGetResolutionsImperatively } from '../api/useSeismicResolutionController';

export function useUpdateLayer(){
    const urlParams = getParamsFromUrl();
    const {tenantConfig} = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration
    }));

    const jwt = useSessionStore(state => state.jwtToken);
    const flipedByDefaultRef = useRef(false);
    const currentVolTokenRef = useRef('');

    const { lineNumber, lineType, setLineNumber, setLineIdentifier, setLineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        lineType: state.lineType,
        setLineIdentifier: state.setLineIdentifier,
        setLineNumber: state.setLineNumber,
        setLineType: state.setLineType
    }));

    const showSnackbar = useSnackbarStore(state => state.showSnackbar);

    const { mainSeismicLayer, swipeSeismic: swipeSeismicLayer, overlaySeismic: overlaySeismicLayer, mapSize, map, resolutions, debugLayer, prepareRenderLimit, setBackgroundColor } = useMapStore(state => ({
        debugLayer: state.debugLayer,
        mainSeismicLayer: state.mainSeismicLayer,
        map: state.map,
        mapSize: state.mapSize,
        resolutions: state.resolutions,
        overlaySeismic: state.overlaySeismic,
        swipeSeismic: state.swipeSeismic,
        prepareRenderLimit: state.preparedTraceLimit,
        setBackgroundColor: state.setBackgroundColor,
    }));

    const getSliceRotation = useGetSliceRotation();

    const tileSize = useTileSize();

    const { volumeToken, calculator,  volumeTokenSwipe, swipeValue, volumeTokenOverlay, surveyMetadataOverlay, isInverted, isAntialiasApplied, sortOption,
        surveyMetadata, scale, colorbar, /*gain,*/ absoluteGain, rangeTrace, surveyMetadataSwipe, setIsSeismicLoading, setCalculator, setIsVerticallyInverted,
        baseCalculatedAttributeId, swipeCalculatedAttributeId, overlayCalculatedAttributeId, traceRenderLimits, setScaleX, setScaleY,
    } = useSeismicStore(state => ({
        calculator: state.calculator,
        isInverted: state.isInverted,
        isAntialiasApplied: state.isAntialiasApplied,
        sortOption: state.sortOption,
        colorbar: state.colorbar,
        volumeToken: state.volumeToken,
        volumeTokenOverlay: state.volumeTokenOverlay,
        volumeTokenSwipe: state.volumeTokenSwipe,
        //gain: state.gain,
        rangeTrace: state.rangeTrace,
        scale: state.scale,
        surveyMetadata: state.surveyMetadata,
        absoluteGain: state.absoluteGain,
        surveyMetadataOverlay: state.surveyMetadataOverlay,
        surveyMetadataSwipe: state.surveyMetadataSwipe,
        swipeValue: state.swipeValue,
        setIsSeismicLoading: state.setIsLoading,
        setCalculator: state.setCalculator,
        setIsVerticallyInverted: state.setIsVerticallyInverted,
        baseCalculatedAttributeId: state.baseCalculatedAttributeId,
        swipeCalculatedAttributeId: state.swipeCalculatedAttributeId,
        overlayCalculatedAttributeId: state.overlayCalculatedAttributeId,
        restrictedAreaLayer: state.restrictedAreaLayer,
        traceRenderLimits: state.traceRenderLimits,
        setScaleX: state.setScaleX,
        setScaleY: state.setScaleY,
    }));

    const { overlayGain, swipeGain, overlayOpacity, swipeOpacity, overlayColorbar, swipeColorbar } = UseAttributesStore(state => ({
        overlayGain: state.overlayGain,
        swipeGain: state.swipeGain,
        overlayOpacity: state.overlayOpacity,
        swipeOpacity: state.swipeOpacity,
        overlayColorbar: state.overlayColorbar,
        swipeColorbar: state.swipeColorbar,
    }));

    const prepareMainLayerRender = usePrepareRender();
    const prepareOverlayLayerRender = usePrepareRender();
    const prepareSwipeLayerRender = usePrepareRender();

    const lastLineTypeRef = useRef<LineType | null>(null);
    const lastLineNumberRef = useRef<number | null>(null);

    const lastScaleRef = useRef([scale.x, scale.y]);

    const isBaseCalcAttributeFirstValueRef = useRef<boolean>(true);
    const isSurveyMetadataFirstValueRef = useRef<boolean>(true);

    const scaleRef = useRef<{x: number, y: number}>(scale);

    const { mutate: getResolutions } = useGetResolutionsImperatively();

    const lineTypeFirstUpdatedRef = useRef(false);

    const isViewRotatedRef = useRef(false);

    const featureFlags = useFeatureFlags().featureFlags?.seismic2D;

    const getViewRotation = useCallback((currentRotation: number, isInverted: boolean) => {
        if (lineType === LineType.ZSlice && !!map) {
            mainSeismicLayer?.rebuildSource();
            //const currentRotation = map.getView().getRotation();
            if (isInverted) {
                return (currentRotation * -1);
            }
            else {
                return currentRotation;
            }
        }

        return 0;
    }, [lineType, mainSeismicLayer, map]);

    const updateMainSeismicLayer = useCallback(async (doRenderPreparing: boolean = true, rebuildSource: boolean = false) => {
        //map?.updateSize();
        if (mainSeismicLayer && !!calculator && !!surveyMetadata){
            let newAbsoluteGain = absoluteGain;
            const mainSeismicLayerParams : IGeopostSeismicLayerParams = {
                ...mainSeismicLayer.getParams(),
                colorbar: colorbar?.Name,
                isInverted: flipedByDefaultRef.current !== isInverted /* <---- OR Exclusivo */,
                isAntialiasApplied: isAntialiasApplied,
                sortOption: sortOption,
                lineNumber: lineNumber,
                surveyInlineStart: surveyMetadata?.Survey3DInfo.InlineStart!,
                surveyInlineEnd: surveyMetadata?.Survey3DInfo.InlineEnd!,
                surveyXlineStart: surveyMetadata?.Survey3DInfo.XlineStart!,
                surveyXlineEnd: surveyMetadata?.Survey3DInfo.XlineEnd!,
                surveyInlineIncrement: surveyMetadata?.Survey3DInfo.InlineIncrement!,
                surveyXlineIncrement: surveyMetadata?.Survey3DInfo.XlineIncrement!,
                numberOfTraces: surveyMetadata?.Header.NumberOfTraces,
                absoluteGain: newAbsoluteGain,
                volumeToken: volumeToken ? volumeToken : '',
                attributeCalculationMethod: baseCalculatedAttributeId,
                volumeSamplesPerTrace: surveyMetadata?.Header.SamplesPerTrace,
                sampleInterval: calculator.calculateSampleInterval(surveyMetadata),
                baseSampleInterval: calculator.calculateSampleInterval(surveyMetadata),
                cdpDimensionName: surveyMetadata.DimensionNames.Cdp,
                xlineDimensionName: surveyMetadata.DimensionNames.Xline,
                inlineDimensionName: surveyMetadata.DimensionNames.Inline,
                sampleDimensionName: surveyMetadata.DimensionNames.Sample,
                filePath: surveyMetadata.VolumePath,
                baseInlineStart: surveyMetadata.Survey3DInfo.InlineStart,
                baseInlineEnd: surveyMetadata.Survey3DInfo.InlineEnd,
                baseXlineStart: surveyMetadata.Survey3DInfo.XlineStart,
                baseXlineEnd: surveyMetadata.Survey3DInfo.XlineEnd,
                baseInlineIncrement: surveyMetadata.Survey3DInfo.InlineIncrement,
                baseXlineIncrement: surveyMetadata.Survey3DInfo.XlineIncrement,
            };

            if (doRenderPreparing) {
                mainSeismicLayerParams.renderLimits = [];
            }
            if (urlParams.forceDisableSamplesCache || featureFlags?.DisableSamplesCache || !tenantConfig?.useSamplesMatrixCache) {
                mainSeismicLayerParams.renderLimits = [[-Infinity, +Infinity]];
            }

            if (colorbar.Name === 'binary' && baseCalculatedAttributeId === 1) {
                setBackgroundColor('#FFFFFF');
            } else {
                setBackgroundColor(colorbar.Background);
            }

            mainSeismicLayer.updateParams(mainSeismicLayerParams);

            const prepareRender = doRenderPreparing && !featureFlags?.DisableSamplesCache && !urlParams.forceDisableSamplesCache && tenantConfig?.useSamplesMatrixCache;

            if (prepareRender) {
                mainSeismicLayer.updateParams({
                    renderLimits: []
                }, false);
            }

            if (rebuildSource) {
                const extent = calculator.calculateExtent(surveyMetadata, scale, rangeTrace);
                let center = calculator.calculateCenter(extent, surveyMetadata, scale, rangeTrace);
                const defaultTraceToShow = surveyMetadata.Survey3DInfo.DefaultTraceToShow;

                if (!!defaultTraceToShow && (lineType === LineType.Inline || lineType === LineType.Line2D)) {
                    const lineStart = calculator.getMinTrace(surveyMetadata);
                    const lineEnd = calculator.getMaxTrace(surveyMetadata);
                    const lineIncrement = calculator.getLineIncrement(surveyMetadata);
                    const defaultTraceToShowX = CalculateXPositionWithIncrement(defaultTraceToShow, lineStart, lineEnd, lineIncrement,
                        lineStart, isInverted, scale.x);
                    center = [defaultTraceToShowX, center[1]];
                }

                const rotation = getViewRotation(map!.getView().getRotation()!, isInverted);
                map?.updateParams({ extent, center });
                map?.rebuildView();
                map?.getView().setRotation(rotation);
                mainSeismicLayer.updateParams({ extent });
                mainSeismicLayer.rebuildSource();
            }
            else {
                mainSeismicLayer.reload();
            }

            if (prepareRender) {
                //setIsSeismicLoading(true);

                prepareMainLayerRender(mainSeismicLayer, {
                    ...mainSeismicLayerParams,
                    lineNumber,
                    volumeToken: volumeToken ?? surveyMetadata?.VolumeToken!,
                }, mainSeismicLayerParams.loadingLayer!, false, lineType, true);
            }
        }
    }, [mainSeismicLayer, calculator, surveyMetadata, absoluteGain, colorbar.Name, colorbar.Background, isInverted, isAntialiasApplied, sortOption, lineNumber, volumeToken, baseCalculatedAttributeId, urlParams.forceDisableSamplesCache, featureFlags?.DisableSamplesCache, tenantConfig?.useSamplesMatrixCache, setBackgroundColor, scale, rangeTrace, lineType, getViewRotation, map, prepareMainLayerRender]);

    useEffect(() => {
        if (!!map) {
            map.getView().setRotation(getViewRotation(map.getView().getRotation(), isInverted));
        }
    }, [isInverted, map]);

    useEffect(() => {
        updateMainSeismicLayer(false);
    }, [colorbar?.Name, /*gain,*/ absoluteGain, isAntialiasApplied, sortOption]);

    useEffect(() => {
        mainSeismicLayer?.updateParams({ isInverted: isInverted }).reload();
        swipeSeismicLayer?.updateParams({ isInverted: isInverted }).reload();
        overlaySeismicLayer?.updateParams({ isInverted: isInverted }).reload();
    }, [isInverted]);

    useEffect(() => {
        if (!baseCalculatedAttributeId && isBaseCalcAttributeFirstValueRef.current) {
            return;
        }
        updateMainSeismicLayer(!isBaseCalcAttributeFirstValueRef.current);
        if (isBaseCalcAttributeFirstValueRef.current) {
            isBaseCalcAttributeFirstValueRef.current = false;
        }
    }, [baseCalculatedAttributeId]);

    useEffect(() => {
        overlaySeismicLayer?.updateParams({ renderLimits: [] }).rebuildSource();
    }, [volumeTokenOverlay]);

    useEffect(() => {
        swipeSeismicLayer?.updateParams({ renderLimits: [] }).rebuildSource();
    }, [volumeTokenSwipe]);

    useEffect(() => {
        if (!!surveyMetadata && !!surveyMetadata.VolumeToken) {
            updateMainSeismicLayer(true, true);

            const url = new URL(window.location.href);
            const volumeIdentifier = surveyMetadata.VolumeToken.toString();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (volumeIdentifier.trim() !== '' && !isNaN(volumeIdentifier as any)) {
                url.searchParams.delete('volumeToken');
                url.searchParams.set('volumeId', volumeIdentifier);
            }
            else {
                url.searchParams.delete('volumeId');
                url.searchParams.set('volumeToken', volumeIdentifier);
            }
            // Update the URL in-place without reloading the page
            // eslint-disable-next-line no-restricted-globals
            history.replaceState(null, '', url.toString());
        }
    }, [surveyMetadata]);

    useEffect(() => {
        if (!!lineNumber) {
            if (!lastLineNumberRef.current && lineNumber > 0) {
                lastLineNumberRef.current = lineNumber;
                return;
            }
            mainSeismicLayer?.getParams().loadingLayer?.clearAllErrors();
            updateMainSeismicLayer();
        }
    }, [lineNumber]);

    useEffect(() => {
        if (overlaySeismicLayer && (lineNumber > 0 || lineType === LineType.Line2D) && !!calculator && !!surveyMetadataOverlay && !!calculator) {
            overlaySeismicLayer.getParams().loadingLayer?.clearAllErrors();
            let opacity = overlayOpacity;
            overlaySeismicLayer.setVisible(false);
            overlaySeismicLayer.setOpacity(opacity);
            const extent = calculator.calculateExtent(surveyMetadata!, scale, rangeTrace);
            const surveyLineStart = calculator.getMinTrace(surveyMetadataOverlay);
            const surveyLineEnd = calculator.getMaxTrace(surveyMetadataOverlay);
            //const extent = mainSeismicLayer?.getParams().extent;
            overlaySeismicLayer.updateParams({
                lineNumber: lineNumber,
                isInverted: isInverted,
                verticallyInverted: mainSeismicLayer?.getParams().verticallyInverted,
                extent,
                isAntialiasApplied: isAntialiasApplied,
                absoluteGain: overlayGain,
                volumeToken: surveyMetadataOverlay?.VolumeToken ? surveyMetadataOverlay?.VolumeToken : '',
                sampleInterval: calculator.calculateSampleInterval(surveyMetadataOverlay),
                baseSampleInterval: calculator.calculateSampleInterval(surveyMetadata!),
                surveyLineStart,
                surveyLineEnd,
                surveyInlineStart: surveyMetadataOverlay?.Survey3DInfo.InlineStart,
                surveyInlineEnd: surveyMetadataOverlay?.Survey3DInfo.InlineEnd,
                surveyXlineStart: surveyMetadataOverlay?.Survey3DInfo.XlineStart,
                surveyXlineEnd: surveyMetadataOverlay?.Survey3DInfo.XlineEnd,
                surveyInlineIncrement: surveyMetadataOverlay?.Survey3DInfo.InlineIncrement,
                surveyXlineIncrement: surveyMetadataOverlay?.Survey3DInfo.XlineIncrement,
                numberOfTraces: surveyMetadataOverlay?.Header.NumberOfTraces,
                volumeSamplesPerTrace: surveyMetadataOverlay?.Header.SamplesPerTrace ?? 0,
                filePath: surveyMetadataOverlay?.VolumePath,
                attributeCalculationMethod: overlayCalculatedAttributeId,
                baseInlineStart: surveyMetadata?.Survey3DInfo.InlineStart,
                baseInlineEnd: surveyMetadata?.Survey3DInfo.InlineEnd,
                baseXlineStart: surveyMetadata?.Survey3DInfo.XlineStart,
                baseXlineEnd: surveyMetadata?.Survey3DInfo.XlineEnd,
                baseInlineIncrement: surveyMetadata?.Survey3DInfo.InlineIncrement,
                baseXlineIncrement: surveyMetadata?.Survey3DInfo.XlineIncrement,
                cdpDimensionName: surveyMetadataOverlay?.DimensionNames.Cdp,
                xlineDimensionName: surveyMetadataOverlay?.DimensionNames.Xline,
                inlineDimensionName: surveyMetadataOverlay?.DimensionNames.Inline,
                sampleDimensionName: surveyMetadataOverlay?.DimensionNames.Sample,
            }, false);
            const overlaySeismicLayerParams = overlaySeismicLayer.getParams();
            //map?.updateParams({ extent: newExtent, center: newCenter }).rebuildView();
            //mainSeismicLayer?.updateParams({extent: newExtent}).rebuildSource();
            if (!featureFlags?.DisableSamplesCache && !urlParams.forceDisableSamplesCache && tenantConfig?.useSamplesMatrixCache){
                overlaySeismicLayer.updateParams({ renderLimits: [] }, false);
                overlaySeismicLayer.rebuildSource();
                prepareOverlayLayerRender(overlaySeismicLayer, overlaySeismicLayerParams, overlaySeismicLayerParams.loadingLayer, false, lineType);
            } else {
                overlaySeismicLayer.updateParams({ renderLimits: [[-Infinity, +Infinity]]}).rebuildSource();
            }

            overlaySeismicLayer.setVisible(true);
        } else if (overlaySeismicLayer) {
            overlaySeismicLayer.setVisible(false);
        }
    }, [surveyMetadataOverlay, lineNumber, overlayCalculatedAttributeId, isAntialiasApplied]);

    useEffect(() => {
        setLineIdentifier(lineType, lineNumber);

        if (swipeSeismicLayer && !!surveyMetadataSwipe && (lineNumber > 0 || lineType === LineType.Line2D) && !!calculator) {
            swipeSeismicLayer.getParams().loadingLayer?.clearAllErrors();
            swipeSeismicLayer.setOpacity(swipeOpacity);
            const extent = calculator.calculateExtent(surveyMetadata!, scale, rangeTrace);
            const surveyLineStart = calculator.getMinTrace(surveyMetadataSwipe);
            const surveyLineEnd = calculator.getMaxTrace(surveyMetadataSwipe);
            swipeSeismicLayer.setVisible(true);
            swipeSeismicLayer.updateParams({
                isSwiped: true,
                lineNumber: lineNumber,
                isInverted: isInverted,
                extent,
                verticallyInverted: mainSeismicLayer?.getParams().verticallyInverted,
                isAntialiasApplied: isAntialiasApplied,
                absoluteGain: swipeGain,
                volumeToken: surveyMetadataSwipe?.VolumeToken ? surveyMetadataSwipe?.VolumeToken : '',
                sampleInterval: calculator.calculateSampleInterval(surveyMetadataSwipe),
                baseSampleInterval: calculator.calculateSampleInterval(surveyMetadata!),
                surveyLineStart,
                surveyLineEnd,
                surveyInlineStart: surveyMetadataSwipe?.Survey3DInfo.InlineStart,
                surveyInlineEnd: surveyMetadataSwipe?.Survey3DInfo.InlineEnd,
                surveyXlineStart: surveyMetadataSwipe?.Survey3DInfo.XlineStart,
                surveyXlineEnd: surveyMetadataSwipe?.Survey3DInfo.XlineEnd,
                surveyInlineIncrement: surveyMetadataSwipe?.Survey3DInfo.InlineIncrement,
                surveyXlineIncrement: surveyMetadataSwipe?.Survey3DInfo.XlineIncrement,
                numberOfTraces: surveyMetadataSwipe?.Header.NumberOfTraces,
                volumeSamplesPerTrace: surveyMetadataSwipe?.Header.SamplesPerTrace ?? 0,
                filePath: surveyMetadataSwipe?.VolumePath,
                attributeCalculationMethod: swipeCalculatedAttributeId,
                baseInlineStart: surveyMetadata?.Survey3DInfo.InlineStart,
                baseInlineEnd: surveyMetadata?.Survey3DInfo.InlineEnd,
                baseXlineStart: surveyMetadata?.Survey3DInfo.XlineStart,
                baseXlineEnd: surveyMetadata?.Survey3DInfo.XlineEnd,
                baseInlineIncrement: surveyMetadata?.Survey3DInfo.InlineIncrement,
                baseXlineIncrement: surveyMetadata?.Survey3DInfo.XlineIncrement,
                cdpDimensionName: surveyMetadataSwipe?.DimensionNames.Cdp,
                xlineDimensionName: surveyMetadataSwipe?.DimensionNames.Xline,
                inlineDimensionName: surveyMetadataSwipe?.DimensionNames.Inline,
                sampleDimensionName: surveyMetadataSwipe?.DimensionNames.Sample,
            }, false);
            const swipeSeismicLayerParams = swipeSeismicLayer.getParams();
            if (!featureFlags?.DisableSamplesCache && !urlParams.forceDisableSamplesCache && tenantConfig?.useSamplesMatrixCache){
                swipeSeismicLayer.updateParams({ renderLimits: [] }, false);
                swipeSeismicLayer.rebuildSource();
                prepareSwipeLayerRender(swipeSeismicLayer, swipeSeismicLayerParams, swipeSeismicLayerParams.loadingLayer, false, lineType);
            } else {
                swipeSeismicLayer.updateParams({ renderLimits: [[-Infinity, +Infinity]]}).rebuildSource();
            }
        } else if (swipeSeismicLayer) {
            swipeSeismicLayer.setVisible(false);
        }
    }, [surveyMetadataSwipe, lineNumber, isAntialiasApplied, swipeCalculatedAttributeId]);

    useEffect(() => {
        if (!!swipeSeismicLayer && volumeTokenSwipe !== '-') {
            swipeSeismicLayer.updateParams({
                absoluteGain: swipeGain,
                colorbar: swipeColorbar.Name
            }).reload();
        }
    }, [swipeColorbar, swipeGain]);

    useEffect(() => {
        if (!!overlaySeismicLayer && volumeTokenOverlay !== '-') {
            overlaySeismicLayer.updateParams({
                absoluteGain: overlayGain,
                colorbar: overlayColorbar.Name,
            }).reload();
        }
    }, [overlayGain, overlayColorbar]);

    useEffect(() => {
        if (!!overlaySeismicLayer && volumeTokenOverlay !== '') {
            overlaySeismicLayer?.setOpacity(overlayOpacity);
        }
    }, [overlayOpacity]);

    useEffect(() => {
        if (!!swipeSeismicLayer && volumeTokenSwipe !== '') {
            swipeSeismicLayer?.setOpacity(swipeOpacity);
        }
    }, [swipeOpacity]);

    const getTraceRenderLimitExtent = useCallback((traceRenderLimit: [number, number]) => {
        if (!surveyMetadata || !calculator) {
            return { extent: [0,0,0,0], startTrace: 0, endTrace: 0 };
        }

        const extent = calculator.calculateExtent(surveyMetadata, scale, rangeTrace);
        const minTrace = calculator.getMinTrace(surveyMetadata);
        const maxTrace = calculator.getMaxTrace(surveyMetadata);
        const increment = calculator.getLineIncrement(surveyMetadata);
        const totalTraces = calculator.getTotalTraces(surveyMetadata);

        let [ startTrace, endTrace ] = traceRenderLimit;

        const limit = [
            calculateXPosition(startTrace, totalTraces, minTrace, maxTrace, increment, minTrace, isInverted, scale.x),
            calculateXPosition(endTrace, totalTraces, minTrace, maxTrace, increment, minTrace, isInverted, scale.x),
        ];

        if (isInverted) {
            [ startTrace, endTrace ] = [ maxTrace - endTrace + minTrace, maxTrace - startTrace + minTrace ];
        }

        extent[0] = !isInverted ? limit[0] : limit[1];
        extent[2] = !isInverted ? limit[1] : limit[0];

        return { extent, startTrace, endTrace };
    }, [calculator, isInverted, rangeTrace, scale.x, surveyMetadata]);

    const windowUpdateCallback = useCallback(() => {
        if (!!surveyMetadata && !!calculator && !!map) {
            const totalTraces = calculator.getTotalTraces(surveyMetadata);
            setIsSeismicLoading(true);
            getSliceRotation(lineType, surveyMetadata).then(({ needsFlip, rotation, reference: rotationReference, needsVerticalFlip, inlineScale, xlineScale }) => {
                const rotated = rotation % Math.PI !== 0;
                getResolutions({
                    scaleY: scale.y,
                    screenHeight: window.innerHeight,
                    tileHeight: tileSize.height,
                    tileWidth: tileSize.width,
                    totalSamples: lineType !== LineType.ZSlice ? surveyMetadata.Header.SamplesPerTrace : rotated ? getSurveyExtentDistance(surveyMetadata, scale.x, scale.y) : surveyMetadata.Survey3DInfo.TotalXlines,
                    totalTraces,
                    traceSkip: calculator.getSkipTrace(surveyMetadata)
                }, {
                    onSuccess: (data) => {

                        mainSeismicLayer?.updateParams({
                            verticallyInverted: needsVerticalFlip,
                            resolutions: data.Resolutions,
                            tileDimensionsPerResolution: data.TileDimensions,
                        });

                        setIsSeismicLoading(false);
                        const extent = calculator.calculateExtent(surveyMetadata, scale, rangeTrace);
                        const center = calculator.calculateCenter(extent, surveyMetadata, scale, rangeTrace);
                        map.updateParams({ resolutions: data.Resolutions, extent, center }).rebuildView();
                        mainSeismicLayer?.rebuildSource();
                        map.getView().setRotation(getViewRotation(rotation, isInverted));
                    },
                    onError: () => {
                        setIsSeismicLoading(false);
                    }
                });
            }).catch(() => {
                setIsSeismicLoading(false);
            });

        }
    }, [calculator, getResolutions, lineType, map, rangeTrace, scale, setIsSeismicLoading, surveyMetadata, tileSize.height, tileSize.width]);

    useEffect(() => {
        if (mainSeismicLayer && map && surveyMetadata && calculator){
            const extent = !traceRenderLimits ? calculator.calculateExtent(surveyMetadata, scale, rangeTrace) : getTraceRenderLimitExtent(traceRenderLimits[0]).extent;
            const center = map.getView().getCenter()!; //calculator.calculateCenter(extent, surveyMetadata, scale, rangeTrace);

            //center[0] = center[0] / scaleRef.current.x * scale.x;
            //center[1] = center[1] / scaleRef.current.y * scale.y;

            scaleRef.current = scale;

            /*if (traceRenderLimits && traceRenderLimits?.length > 0) {
                const limit = traceRenderLimits && traceRenderLimits?.length > 0 ? traceRenderLimits[0] : [0,0];
                extent[0] = limit[0] * scale.x;
                extent[2] = limit[1] * scale.x;
                center[0] = (limit[0] + limit[1]) / 2;
            }*/

            const rotation = map.getView().getRotation();
            axios.get<IResponseAPI<IResolutionsResponse>>(`${tenantConfig?.endpoints.metadata}/Seismic/SeismicResolution/Get`, {
                params: {
                    tileWidth: tileSize.width,
                    screenHeight: mapSize.height,
                    totalSamples: lineType !== LineType.ZSlice ? surveyMetadata.Header.SamplesPerTrace : rotation !== 0 ? getSurveyExtentDistance(surveyMetadata, scale.x, scale.y) : surveyMetadata.Survey3DInfo.TotalXlines,
                    scaleY: scale.y,
                    traceSkip: mainSeismicLayer.getParams().skipTraceCount,
                    tileHeight: tileSize.height,
                    totalTraces: lineType === LineType.Xline ? surveyMetadata.Survey3DInfo.TotalInlines : surveyMetadata.Survey3DInfo.TotalXlines,
                }
            }).then(response => {
                //map.updateParams({extent: extent}).rebuildView();

                mainSeismicLayer.updateParams({
                    scaleX: scale.x,
                    scaleY: scale.y,
                    extent,
                });

                overlaySeismicLayer?.updateParams({
                    scaleX: scale.x,
                    scaleY: scale.y,
                    extent,
                });

                swipeSeismicLayer?.updateParams({
                    scaleX: scale.x,
                    scaleY: scale.y,
                    extent,
                });

                //map.getView().adjustRotation(rotation, rotationReference);
                const center = [(extent[2] - extent[0]) / 2 + extent[0], extent[3] - extent[1] / 2 + extent[1]];
                map?.getView().setCenter(center);

                const view = map.getView();
                //let resolutions = view.getResolutions()!;
                let { data: { Result: { Resolutions: resolutions, TileDimensions: tileSizes } } } = response;

                map.updateParams({ resolutions: resolutions, extent: extent, center: center }).rebuildView();
                map.getView().setRotation(rotation);
                map.getView().setCenter(center);

                mainSeismicLayer.updateParams({ tileDimensionsPerResolution: tileSizes, resolutions: resolutions });
                overlaySeismicLayer?.updateParams({ tileDimensionsPerResolution: tileSizes, resolutions: resolutions });
                swipeSeismicLayer?.updateParams({ tileDimensionsPerResolution: tileSizes, resolutions: resolutions });
                lastScaleRef.current = [scale.x, scale.y];
                mainSeismicLayer.rebuildSource();
                overlaySeismicLayer?.rebuildSource();
                swipeSeismicLayer?.rebuildSource();

                if (debugLayer){
                    debugLayer.updateTileGrid(mainSeismicLayer.tileGrid!);
                }
            }
            );
        }
    }, [scale.x, scale.y]);

    useEffect(() => {
        if (mainSeismicLayer && map && surveyMetadata && calculator && !!traceRenderLimits){
            /*mainSeismicLayer.updateParams({
                renderLimits: traceRenderLimits ?? []
            }).reload();
            */
            const {extent, startTrace, endTrace} = getTraceRenderLimitExtent(traceRenderLimits[0]);
            //const center = map.getView().getCenter()!;

            //center[0] = center[0] / scaleRef.current.x * scale.x;
            //center[1] = center[1] / scaleRef.current.y * scale.y;

            const mainSeismicLayerParams: IGeopostSeismicLayerParams = {
                ...mainSeismicLayer.getParams(),
                extent: extent,
                traceRangeStart: startTrace,
                traceRangeEnd: endTrace,
                useTraceRange: true,
            };
            const currentCenter = map?.getView().getCenter();

            map.updateParams({ extent: extent }).rebuildView();
            map?.getView().setCenter([ (extent[2] - extent[0]) / 2 + extent[0], !!currentCenter ? currentCenter[1] : 0 ]);

            mainSeismicLayer?.updateParams(mainSeismicLayerParams).rebuildSource();
        }
    }, [traceRenderLimits, isInverted]);

    useEffect(() => {
        if (mainSeismicLayer && map && surveyMetadata && calculator && !surveyMetadata.IsIndexed){
            const extent = calculator.calculateExtent(surveyMetadata, scale, rangeTrace);
            const center = calculator.calculateCenter(extent, surveyMetadata, scale, rangeTrace);

            getSliceRotation(lineType, surveyMetadata).then(
                ({ needsFlip, rotation, reference: rotationReference, needsVerticalFlip, inlineScale, xlineScale }) => {
                    map.updateParams({
                        center: center,
                        extent: extent
                    }).rebuildView();

                    mainSeismicLayer.updateParams({
                        extent: extent,
                        traceRangeEnd: rangeTrace.end,
                        traceRangeStart: rangeTrace.initial,
                        isInverted,
                    }).rebuildSource();

                    overlaySeismicLayer?.updateParams({
                        extent: extent,
                        traceRangeEnd: 20000,
                        traceRangeStart: 1,
                    }).rebuildSource();

                    swipeSeismicLayer?.updateParams({
                        extent: extent,
                        traceRangeEnd: rangeTrace.end,
                        traceRangeStart: rangeTrace.initial,
                    }).rebuildSource();

                    map.getView().adjustRotation(rotation, rotationReference);
                    map.getView().setCenter(center);

                    if (debugLayer){
                        debugLayer.updateTileGrid(mainSeismicLayer.tileGrid!);
                    }
                }
            ).catch(() => {
                // avoid an unhandled rejection if the reference lines fail to load here too
            });
        }
    }, [rangeTrace, scale]);

    useEffect(() => {

        if (!surveyMetadata || !mainSeismicLayer || !map || lineType === LineType.Line2D){
            return;
        }
        //return;

        const newCalculator = SeismicCalculatorFactory.buildCalculator(surveyMetadata.IsIndexed, lineType, surveyMetadata.Type);

        const traceSkip = newCalculator?.getSkipTrace(surveyMetadata);

        SeismicCalculatorFactory.updateLineTypeCalculator(newCalculator!, lineType);

        let rotated = false;
        setIsSeismicLoading(true);
        getSliceRotation(lineType, surveyMetadata).then(
            ({ needsFlip, rotation, reference: rotationReference, needsVerticalFlip, inlineScale, xlineScale }) => {
                //needsVerticalFlip = false;
                flipedByDefaultRef.current = false;
                if (needsFlip) {
                    flipedByDefaultRef.current = true;
                }
                rotated = rotation % Math.PI !== 0;

                mainSeismicLayer.rebuildSource();

                axios.get<IResponseAPI<IResolutionsResponse>>(`${tenantConfig?.endpoints.metadata}/Seismic/SeismicResolution/Get`, {
                    params: {
                        tileWidth: tileSize.width,
                        screenHeight: mapSize.height,
                        totalSamples: lineType !== LineType.ZSlice ? surveyMetadata.Header.SamplesPerTrace : rotated ? getSurveyExtentDistance(surveyMetadata, scale.x, scale.y) : surveyMetadata.Survey3DInfo.TotalXlines,
                        scaleY: scale.y,
                        traceSkip: traceSkip,
                        tileHeight: tileSize.height,
                        totalTraces: lineType === LineType.Xline ? surveyMetadata.Survey3DInfo.TotalInlines : surveyMetadata.Survey3DInfo.TotalXlines,
                    }
                }).then(async response => {
                    const resolutions = response.data.Result.Resolutions;
                    const tileDimensionsPerResolution = response.data.Result.TileDimensions;
                    const { lineNumber: lineNumberParam, lineType: lineTypeParam } = getParamsFromUrl();
                    const lineNumber = !!lineNumberParam && lineTypeParam === lineType ? lineNumberParam : newCalculator!.calculateLineStart(surveyMetadata);
                    const extent = newCalculator!.calculateExtent(surveyMetadata, scale, rangeTrace);
                    const center = newCalculator!.calculateCenter(extent, surveyMetadata, scale, rangeTrace);
                    const lineIncrement = newCalculator!.getLineIncrement(surveyMetadata);
                    let surveyLineStart = newCalculator!.getMinTrace(surveyMetadata);
                    let surveyLineEnd = newCalculator!.getMaxTrace(surveyMetadata);
                    const [ baseLineStart, baseLineEnd ] = [ surveyLineStart, surveyLineEnd ];
                    if (lineType === LineType.ZSlice) {
                        const zSliceTileSourceStrategy = new ZSliceTileSourceStrategy(tenantConfig!.endpoints.render, surveyMetadata.Survey3DInfo.XlineStart!, surveyMetadata.Survey3DInfo.XlineIncrement!);
                        zSliceTileSourceStrategy.onSettleTileRequest = mainSeismicLayer.tileSourceStrategy.onSettleTileRequest;
                        mainSeismicLayer.setTileSourceStrategy(zSliceTileSourceStrategy);
                        overlaySeismicLayer?.setTileSourceStrategy(zSliceTileSourceStrategy);
                        swipeSeismicLayer?.setTileSourceStrategy(zSliceTileSourceStrategy);
                    } else {
                        const lineTileSourceStrategy = new LineTileSourceStrategy(lineType, tenantConfig!.endpoints.render);
                        lineTileSourceStrategy.onSettleTileRequest = mainSeismicLayer.tileSourceStrategy.onSettleTileRequest;
                        mainSeismicLayer.setTileSourceStrategy(lineTileSourceStrategy);
                        overlaySeismicLayer?.setTileSourceStrategy(lineTileSourceStrategy);
                        swipeSeismicLayer?.setTileSourceStrategy(lineTileSourceStrategy);
                    }

                    map.updateParams({
                        center: center,
                        extent: extent,
                        resolutions: resolutions,
                    }).rebuildView();

                    const limits : [number, number][] = !featureFlags?.DisableSamplesCache && !urlParams.forceDisableSamplesCache? [] : [[+Infinity, -Infinity]];

                    const mainSeismicLayerParams = {
                        ...mainSeismicLayer.getParams(),
                        extent: extent,
                        isAntialiasApplied: isAntialiasApplied,
                        lineType: lineType,
                        lineNumber: lineNumber,
                        sourceApi: urlParams.sourceApi,
                        volumeToken: volumeToken ? volumeToken : '',
                        resolutions: resolutions,
                        tileDimensionsPerResolution: tileDimensionsPerResolution,
                        lineIncrement: lineIncrement,
                        surveyLineStart: surveyLineStart,
                        surveyLineEnd: surveyLineEnd,
                        sampleInterval: newCalculator!.calculateSampleInterval(surveyMetadata),
                        baseSampleInterval: newCalculator!.calculateSampleInterval(surveyMetadata),
                        numberOfTraces: surveyMetadata.Header.NumberOfTraces,
                        lineStartLimit: surveyLineStart,
                        lineEndLimit: surveyLineStart,
                        verticallyInverted: needsVerticalFlip,
                        renderLimitts: limits,
                        sortOption: sortOption,
                        filePath: surveyMetadata.VolumePath,
                    };

                    mainSeismicLayer.updateParams(mainSeismicLayerParams).rebuildSource();
                    mainSeismicLayer.updateParams({ isInverted: lineType === LineType.ZSlice ? true : isInverted }).reload();

                    if (overlaySeismicLayer) {

                        if (!!surveyMetadataOverlay) {
                            surveyLineStart = lineType === LineType.Xline ? surveyMetadataOverlay.Survey3DInfo.InlineStart : surveyMetadataOverlay.Survey3DInfo.XlineStart;
                            surveyLineEnd = lineType === LineType.Xline ? surveyMetadataOverlay.Survey3DInfo.InlineEnd : surveyMetadataOverlay.Survey3DInfo.XlineEnd;
                        }

                        const overnSeismicLayerParams = {
                            extent: extent,
                            isAntialiasApplied: isAntialiasApplied,
                            lineType: lineType,
                            lineNumber: lineNumber,
                            sourceApi: urlParams.sourceApi,
                            volumeToken: volumeToken ? volumeToken : '',
                            resolutions: resolutions,
                            tileDimensionsPerResolution: tileDimensionsPerResolution,
                            lineIncrement: lineIncrement,
                            surveyLineStart: surveyLineStart,
                            surveyLineEnd: surveyLineEnd,
                            sampleInterval: newCalculator!.calculateSampleInterval(surveyMetadataOverlay ?? surveyMetadata),
                            baseSampleInterval: newCalculator!.calculateSampleInterval(surveyMetadata),
                            numberOfTraces: surveyMetadata.Header.NumberOfTraces,
                            baseLineStart,
                            baseLineEnd,
                            lineStartLimit: surveyLineStart,
                            lineEndLimit: surveyLineEnd,
                            verticallyInverted: needsVerticalFlip && needsVerticalFlip,
                            renderLimitts: limits,
                            sortOption: sortOption,
                        };

                        overlaySeismicLayer.updateParams(overnSeismicLayerParams).rebuildSource();
                        overlaySeismicLayer.updateParams({ isInverted: lineType === LineType.ZSlice ? true : isInverted }).reload();
                    }

                    if (swipeSeismicLayer) {
                        if (!!surveyMetadataSwipe) {
                            surveyLineStart = lineType === LineType.Xline ? surveyMetadataSwipe.Survey3DInfo.InlineStart : surveyMetadataSwipe.Survey3DInfo.XlineStart;
                            surveyLineEnd = lineType === LineType.Xline ? surveyMetadataSwipe.Survey3DInfo.InlineEnd : surveyMetadataSwipe.Survey3DInfo.XlineEnd;
                        }

                        const swipeParams = {
                            extent: extent,
                            isAntialiasApplied: isAntialiasApplied,
                            lineType: lineType,
                            lineNumber: lineNumber,
                            sourceApi: urlParams.sourceApi,
                            volumeToken: volumeToken ? volumeToken : '',
                            resolutions: resolutions,
                            tileDimensionsPerResolution: tileDimensionsPerResolution,
                            lineIncrement: lineIncrement,
                            surveyLineStart: surveyLineStart,
                            surveyLineEnd: surveyLineEnd,
                            baseLineStart,
                            baseLineEnd,
                            sampleInterval: newCalculator!.calculateSampleInterval(surveyMetadataSwipe ?? surveyMetadata),
                            baseSampleInterval: newCalculator!.calculateSampleInterval(surveyMetadata),
                            numberOfTraces: surveyMetadata.Header.NumberOfTraces,
                            lineStartLimit: surveyLineStart,
                            lineEndLimit: surveyLineEnd,
                            verticallyInverted: needsVerticalFlip && needsVerticalFlip,
                            renderLimitts: limits,
                            sortOption: sortOption,
                        };
                        swipeSeismicLayer?.updateParams(swipeParams).rebuildSource();
                    }

                    if (debugLayer){
                        debugLayer.updateTileGrid(mainSeismicLayer.tileGrid!);
                    }

                    lastLineTypeRef.current = lineType;
                    lastLineNumberRef.current = lineNumber;

                    setLineNumber(lineNumber);
                    setLineIdentifier(lineType, lineNumber);
                    setCalculator(newCalculator);

                    map.getView().setRotation(getViewRotation(rotation, isInverted));
                    map.getView().setCenter(center);
                    setIsSeismicLoading(false);
                    setIsVerticallyInverted(needsVerticalFlip);
                    isViewRotatedRef.current = rotated;
                    setScaleX(xlineScale);
                    setScaleY(inlineScale);

                });
            }
        ).catch(() => {
            setIsSeismicLoading(false);
            showSnackbar('error', 'Failed to load zslice for the selected volume.');
            setLineType(LineType.Inline);
        });
    }, [lineType]);

    useEffect(() => {
        mainSeismicLayer?.updateParams({
            accessToken: jwt
        });
        swipeSeismicLayer?.updateParams({
            accessToken: jwt
        });
        overlaySeismicLayer?.updateParams({
            accessToken: jwt
        });
    }, [jwt]);

    const windowUpdateDebouncedCallbackRef = useRef<() => void>();

    useEffect(() => {
        let resizeTimer: ReturnType<typeof setTimeout>|null = null;
        if (!!windowUpdateDebouncedCallbackRef.current) {
            window.removeEventListener('resize', windowUpdateDebouncedCallbackRef.current);
        }
        const newCallback = () => {
            if (!!resizeTimer) {
                clearTimeout(resizeTimer);
            }
            resizeTimer = setTimeout(windowUpdateCallback, 200);
        };
        window.addEventListener('resize', newCallback);
        windowUpdateDebouncedCallbackRef.current = newCallback;
    }, [windowUpdateCallback]);
}
